import React, { useState, useEffect } from 'react';
import { Edit2, Camera, Instagram, Youtube, Twitter, DollarSign, Eye, EyeOff, AlertTriangle, Trash2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { API_URL } from '../../utils/apiUrl';
import { logger } from '../../utils/logger';

// Helper function to format phone number for display
const formatPhoneNumber = (phone) => {
  if (!phone) return '';
  // Remove all non-numeric characters
  const cleaned = phone.replace(/\D/g, '');
  
  // Handle US numbers (10 or 11 digits)
  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  } else if (cleaned.length === 10) {
    return `+1 (${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  // Return as-is if not standard format
  return phone;
};

// Helper function to map profile data to form state
// This ensures consistent mapping between API data and form fields
const mapProfileToFormData = (profile) => {
  return {
    first_name: profile?.first_name || '',
    last_name: profile?.last_name || '',
    username: profile?.username || '',
    display_preference: profile?.display_preference || 'full_name',
    bio: profile?.bio || '',
    instagram_handle: profile?.instagram_handle || '',
    youtube_url: profile?.youtube_url || '',
    x_handle: profile?.x_handle || '',
    venmo_username: profile?.venmo_username || '',
    is_public_profile: profile?.is_public_profile ?? true,
    shipping_address: {
      full_name: profile?.shipping_address?.full_name || '',
      address_line1: profile?.shipping_address?.address_line1 || '',
      address_line2: profile?.shipping_address?.address_line2 || '',
      city: profile?.shipping_address?.city || '',
      state: profile?.shipping_address?.state || '',
      postal_code: profile?.shipping_address?.postal_code || '',
      country: profile?.shipping_address?.country || '',
      phone: profile?.shipping_address?.phone || ''
    },
    privacy_settings: {
      show_instagram: profile?.privacy_settings?.show_instagram ?? true,
      show_youtube: profile?.privacy_settings?.show_youtube ?? true,
      show_x: profile?.privacy_settings?.show_x ?? true,
      show_venmo: profile?.privacy_settings?.show_venmo ?? true,
      show_city_state: profile?.privacy_settings?.show_city_state ?? true,
      show_social_links: profile?.privacy_settings?.show_social_links ?? true  // Master toggle
    }
  };
};

export const ProfileCard = ({ profile, editMode, onEdit, onSave, onCancel, onOpenPhotoModal, onDeleteAccount }) => {
  const { toast } = useToast();
  
  // Initialize form state from profile
  const [formData, setFormData] = useState(() => mapProfileToFormData(profile));

  // CRITICAL FIX: Update form data when profile prop changes or edit mode is entered
  // This ensures the form is always populated with the latest profile data
  useEffect(() => {
    if (profile && editMode) {
      const mappedData = mapProfileToFormData(profile);
      setFormData(mappedData);
      
      // Debug logging — automatically gated to development by logger.debug
      logger.debug('ProfileCard', 'form initialized', {
        first_name: mappedData.first_name,
        bio: mappedData.bio ? `${mappedData.bio.substring(0, 20)}...` : '(empty)',
        has_shipping: !!mappedData.shipping_address?.full_name,
        has_social: !!(mappedData.instagram_handle || mappedData.youtube_url || mappedData.x_handle),
      });
    }
  }, [profile, editMode]);

  // Also update when profile changes even if not in edit mode (for next edit)
  useEffect(() => {
    if (profile) {
      setFormData(mapProfileToFormData(profile));
    }
  }, [profile]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Handle privacy settings toggles
    if (name.startsWith('privacy_')) {
      const fieldName = name.replace('privacy_', '');
      
      // Update form data
      setFormData({
        ...formData,
        privacy_settings: {
          ...formData.privacy_settings,
          [fieldName]: checked
        }
      });
      
      // Show toast notification
      const fieldLabels = {
        'show_instagram': 'Instagram',
        'show_youtube': 'YouTube',
        'show_x': 'X (Twitter)',
        'show_venmo': 'Venmo',
        'show_city_state': 'City/State',
        'show_social_links': 'All Social Links'
      };
      
      const label = fieldLabels[fieldName] || fieldName;
      toast({
        title: checked ? '✓ Visible to Public' : '✓ Hidden from Public',
        description: checked 
          ? `${label} is now visible to Clubhouse members`
          : `${label} is now hidden from Clubhouse members`,
        duration: 3000,
      });
    }
    // Handle nested shipping address fields
    else if (name.startsWith('shipping_')) {
      const fieldName = name.replace('shipping_', '');
      setFormData({
        ...formData,
        shipping_address: {
          ...formData.shipping_address,
          [fieldName]: value
        }
      });
    } 
    // Handle bio with character limit enforcement
    else if (name === 'bio') {
      const limitedValue = value.slice(0, 90); // Enforce 90 character limit
      setFormData({
        ...formData,
        bio: limitedValue
      });
    }
    else {
      setFormData({
        ...formData,
        [name]: type === 'checkbox' ? checked : value
      });
    }
  };

  const handleSubmit = (e) => {
    if (e) e.preventDefault();
    onSave(formData);
  };

  // Photo handling is managed by parent component

  if (editMode) {
    return (
      <form className="profile-card edit-mode" onSubmit={handleSubmit}>
        {/* Header with Title and Action Buttons */}
        <div className="edit-mode-header">
          <h3>Edit Profile</h3>
          <div className="edit-mode-actions">
            <button type="button" className="btn-cancel" onClick={onCancel}>
              Cancel
            </button>
            <button type="submit" className="btn-save">
              Save Changes
            </button>
          </div>
        </div>
        
        {/* Profile Photo Section */}
        <div className="form-group profile-photo-edit-section">
          <div className="photo-edit-container">
            <div className="profile-photo-edit" onClick={onOpenPhotoModal}>
              {profile?.profile_photo_url ? (
                <img src={`${API_URL}${profile.profile_photo_url}`} alt="Profile" />
              ) : (
                <div className="profile-photo-placeholder">
                  <Camera size={32} />
                  <span>Add Photo</span>
                </div>
              )}
              <div className="photo-edit-overlay">
                <Camera size={20} />
                <span>{profile?.profile_photo_url ? 'Edit Photo' : 'Add Photo'}</span>
              </div>
            </div>
          </div>
          <small>Click to {profile?.profile_photo_url ? 'edit or remove' : 'add'} your profile photo</small>
        </div>
        
        <div className="form-row">
          <div className="form-group">
            <label>First Name</label>
            <input
              type="text"
              name="first_name"
              value={formData.first_name}
              onChange={handleChange}
              placeholder="John"
              className="form-input"
            />
          </div>
          
          <div className="form-group">
            <label>Last Name</label>
            <input
              type="text"
              name="last_name"
              value={formData.last_name}
              onChange={handleChange}
              placeholder="Doe"
              className="form-input"
            />
          </div>
        </div>
        
        <div className="form-group">
          <label>Username (optional)</label>
          <input
            type="text"
            name="username"
            value={formData.username}
            onChange={handleChange}
            placeholder="@yourusername"
            className="form-input"
          />
        </div>

        <div className="form-group display-preference-group">
          <label>Display as:</label>
          <div className="radio-group">
            <label className="radio-label">
              <input
                type="radio"
                name="display_preference"
                value="full_name"
                checked={formData.display_preference === 'full_name'}
                onChange={handleChange}
              />
              <span>Full Name</span>
            </label>
            <label className="radio-label">
              <input
                type="radio"
                name="display_preference"
                value="username"
                checked={formData.display_preference === 'username'}
                onChange={handleChange}
              />
              <span>Username</span>
            </label>
            <label className="radio-label">
              <input
                type="radio"
                name="display_preference"
                value="both"
                checked={formData.display_preference === 'both'}
                onChange={handleChange}
              />
              <span>Both</span>
            </label>
          </div>
        </div>
        
        <div className="form-group">
          <label>Bio</label>
          <input
            type="text"
            name="bio"
            value={formData.bio}
            onChange={handleChange}
            maxLength={90}
            placeholder="Tell us about yourself in one sentence..."
            className="form-input"
          />
          <small>{formData.bio.length}/90 characters</small>
        </div>

        <div className="form-group">
          <label>Instagram Handle</label>
          <div className="input-with-icon input-with-toggle">
            <Instagram size={18} />
            <input
              type="text"
              name="instagram_handle"
              value={formData.instagram_handle}
              onChange={handleChange}
              placeholder="@yourusername"
              className="form-input"
            />
            <label className="privacy-toggle-inline">
              <input
                type="checkbox"
                name="privacy_show_instagram"
                checked={formData.privacy_settings.show_instagram}
                onChange={handleChange}
              />
              <span className="toggle-switch-inline"></span>
            </label>
          </div>
        </div>

        <div className="form-group">
          <label>YouTube URL</label>
          <div className="input-with-icon input-with-toggle">
            <Youtube size={18} />
            <input
              type="text"
              name="youtube_url"
              value={formData.youtube_url}
              onChange={handleChange}
              placeholder="https://youtube.com/@yourchannel"
              className="form-input"
            />
            <label className="privacy-toggle-inline">
              <input
                type="checkbox"
                name="privacy_show_youtube"
                checked={formData.privacy_settings.show_youtube}
                onChange={handleChange}
              />
              <span className="toggle-switch-inline"></span>
            </label>
          </div>
        </div>

        <div className="form-group">
          <label>X (Twitter) Handle</label>
          <div className="input-with-icon input-with-toggle">
            <Twitter size={18} />
            <input
              type="text"
              name="x_handle"
              value={formData.x_handle}
              onChange={handleChange}
              placeholder="@yourusername"
              className="form-input"
            />
            <label className="privacy-toggle-inline">
              <input
                type="checkbox"
                name="privacy_show_x"
                checked={formData.privacy_settings.show_x}
                onChange={handleChange}
              />
              <span className="toggle-switch-inline"></span>
            </label>
          </div>
        </div>

        <div className="form-group venmo-field">
          <label>Venmo Username</label>
          <div className="input-with-icon input-with-toggle">
            <DollarSign size={18} />
            <input
              type="text"
              name="venmo_username"
              value={formData.venmo_username}
              onChange={handleChange}
              placeholder="@yourusername"
              className="form-input"
            />
            <label className="privacy-toggle-inline">
              <input
                type="checkbox"
                name="privacy_show_venmo"
                checked={formData.privacy_settings.show_venmo}
                onChange={handleChange}
              />
              <span className="toggle-switch-inline"></span>
            </label>
          </div>
        </div>

        <div className="shipping-address-section">
          <div className="form-group">
            <label>Full Name (for shipping)</label>
            <input
              type="text"
              name="shipping_full_name"
              value={formData.shipping_address.full_name}
              onChange={handleChange}
              placeholder="John Smith"
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label>Shipping Address Line 1</label>
            <input
              type="text"
              name="shipping_address_line1"
              value={formData.shipping_address.address_line1}
              onChange={handleChange}
              placeholder="123 Main Street"
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label>Shipping Address Line 2 (Optional)</label>
            <input
              type="text"
              name="shipping_address_line2"
              value={formData.shipping_address.address_line2}
              onChange={handleChange}
              placeholder="Apt 4B"
              className="form-input"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>City</label>
              <input
                type="text"
                name="shipping_city"
                value={formData.shipping_address.city}
                onChange={handleChange}
                placeholder="San Francisco"
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label>State/Province</label>
              <input
                type="text"
                name="shipping_state"
                value={formData.shipping_address.state}
                onChange={handleChange}
                placeholder="CA"
                className="form-input"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Postal Code</label>
              <input
                type="text"
                name="shipping_postal_code"
                value={formData.shipping_address.postal_code}
                onChange={handleChange}
                placeholder="94102"
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label>Country</label>
              <input
                type="text"
                name="shipping_country"
                value={formData.shipping_address.country}
                onChange={handleChange}
                placeholder="United States"
                className="form-input"
              />
            </div>
          </div>

          <div className="form-group">
            <label>Phone Number</label>
            <input
              type="tel"
              name="shipping_phone"
              value={formData.shipping_address.phone}
              onChange={handleChange}
              placeholder="+1 (555) 123-4567"
              className="form-input"
            />
          </div>
        </div>

        {/* Settings Section */}
        <div className="settings-section">
          <h4 className="settings-section-title">Settings</h4>
          <div className="privacy-toggles-section">
            <div className="privacy-toggle-row">
              <label className="privacy-toggle-compact">
                <input
                  type="checkbox"
                  name="is_public_profile"
                  checked={formData.is_public_profile}
                  onChange={handleChange}
                />
                <span className="toggle-label-compact">
                  Show my profile in Member Directory
                </span>
                <span className="toggle-switch-inline"></span>
              </label>
            </div>

            <div className="privacy-toggle-row">
              <label className="privacy-toggle-compact">
                <input
                  type="checkbox"
                  name="privacy_show_social_links"
                  checked={formData.privacy_settings.show_social_links}
                  onChange={handleChange}
                />
                <span className="toggle-label-compact">
                  Show my social links to the community
                </span>
                <span className="toggle-switch-inline"></span>
              </label>
              <p className="toggle-helper-text">If off, your Instagram/YouTube/X/Venmo links won't be visible on your directory card.</p>
            </div>

            <div className="privacy-toggle-row">
              <label className="privacy-toggle-compact">
                <input
                  type="checkbox"
                  name="privacy_show_city_state"
                  checked={formData.privacy_settings.show_city_state}
                  onChange={handleChange}
                />
                <span className="toggle-label-compact">
                  Show city and state to other members
                </span>
                <span className="toggle-switch-inline"></span>
              </label>
            </div>

            {/* Delete Account Button - Inside Settings */}
            <div className="delete-account-row">
              <button 
                type="button"
                className="btn-delete-account-red"
                onClick={onDeleteAccount}
              >
                <Trash2 size={16} />
                Delete My Account
              </button>
            </div>
          </div>
        </div>

        {/* Full Width Save Button at Bottom */}
        <div className="edit-form-bottom-actions">
          <button 
            type="submit" 
            className="btn-save-full-width"
          >
            Save Changes
          </button>
        </div>
      </form>
    );
  }

  // Helper to check if profile has displayable name
  const hasDisplayableName = profile?.first_name || profile?.last_name || profile?.username;
  const hasSocialLinks = profile?.instagram_handle || profile?.youtube_url || profile?.x_handle || profile?.venmo_username;

  return (
    <>
    <div className="profile-card-wrapper">
      <div className="profile-card-modern">
        {/* Top Section: Avatar + Edit Button */}
        <div className="profile-top-section">
          <div className="profile-avatar-container" onClick={onOpenPhotoModal}>
            <div className="profile-avatar">
              {profile?.profile_photo_url ? (
                <img src={`${API_URL}${profile.profile_photo_url}`} alt="Profile" />
              ) : (
                <div className="avatar-placeholder">
                  <Camera size={40} />
                </div>
              )}
              <div className="avatar-edit-overlay">
                <Camera size={20} />
              </div>
            </div>
          </div>
          
          <button 
            onClick={onEdit} 
            className="btn-edit-modern" 
            title="Edit profile"
            aria-label="Edit profile"
          >
            <Edit2 size={18} />
            <span>Edit Profile</span>
          </button>
        </div>

        {/* Member Info Section - Only render if has content */}
        {(hasDisplayableName || profile?.bio) && (
          <div className="profile-info-section">
            <div className="member-identity">
              {profile?.display_preference === 'both' && profile?.username ? (
                <>
                  <h2 className="member-name">{profile.username}</h2>
                  {(profile?.first_name || profile?.last_name) && (
                    <p className="member-full-name-secondary">
                      {profile?.first_name && profile?.last_name 
                        ? `${profile.first_name} ${profile.last_name}`
                        : profile?.first_name || ''}
                    </p>
                  )}
                </>
              ) : hasDisplayableName && (
                <h2 className="member-name">
                  {profile?.display_preference === 'username' && profile?.username
                    ? profile.username
                    : profile?.first_name && profile?.last_name 
                      ? `${profile.first_name} ${profile.last_name}`
                      : profile?.first_name || profile?.username || 'Member'}
                </h2>
              )}
              {profile?.bio && (
                <p className="member-tagline">{profile.bio}</p>
              )}
            </div>
          </div>
        )}

        {/* Social Links Section - Only render if has social links */}
        {hasSocialLinks && (
          <div className="social-section">
            <h4 className="section-label">Connect</h4>
            <div className="social-links-modern">
              {profile.instagram_handle && (
                <a 
                  href={`https://instagram.com/${profile.instagram_handle}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="social-icon-modern instagram"
                  aria-label="Instagram"
                >
                  <Instagram size={20} />
                </a>
              )}
              {profile.youtube_url && (
                <a 
                  href={profile.youtube_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="social-icon-modern youtube"
                  aria-label="YouTube"
                >
                  <Youtube size={20} />
                </a>
              )}
              {profile.x_handle && (
                <a 
                  href={`https://x.com/${profile.x_handle}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="social-icon-modern twitter"
                  aria-label="X (Twitter)"
                >
                  <Twitter size={20} />
                </a>
              )}
              {profile.venmo_username && (
                <a 
                  href={`https://venmo.com/${profile.venmo_username}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="social-icon-modern venmo"
                  aria-label="Venmo"
                >
                  <DollarSign size={20} />
                </a>
              )}
            </div>
          </div>
        )}
      </div>
    </div>

  </>
  );
};